import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { X, Check, RotateCcw, Anchor, Target, Feather, Headphones } from 'lucide-react';

/**
 * Showing up. Minutes are the unit: two minutes of a ten-minute sit is 20% — honoured, not
 * failed. A ritual with no duration is simply done or not.
 */
export const CompleteSheet = ({ ritual, date, window: slot, onDone, onClose }) => {
  const timed = !!ritual.duration_minutes;
  const [minutes, setMinutes] = useState(
    ritual.minutes_done ?? (timed ? ritual.duration_minutes : 0),
  );
  const [busy, setBusy] = useState(false);

  const send = async (payload) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/companion/rituals/complete`, {
        ritual_id: ritual.ritual_id, date, window: slot, ...payload,
      });
      onDone(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not log that');
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    setBusy(true);
    try {
      const { data } = await axios.delete(
        `${API}/companion/rituals/complete/${ritual.ritual_id}/${date}`);
      onDone(data);
    } catch {
      toast.error('Could not undo that');
    } finally {
      setBusy(false);
    }
  };

  const percent = timed ? Math.min(100, Math.round((minutes / ritual.duration_minutes) * 100)) : 100;

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center
                    bg-slate-950/80 backdrop-blur-sm"
         data-testid="ritual-complete-sheet">
      <div className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border border-white/10
                      bg-[#0b1020] p-6 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-amber-300/70">
              {new Date(`${date}T12:00:00`).toLocaleDateString(undefined,
                { weekday: 'long', month: 'short', day: 'numeric' })}
            </p>
            <h2 className="text-base md:text-lg font-semibold text-white mt-1">{ritual.title}</h2>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors"
                  data-testid="ritual-complete-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-2 text-xs text-white/55">
          {ritual.anchor && (
            <p className="flex items-start gap-2">
              <Anchor className="w-3.5 h-3.5 mt-0.5 text-amber-300/60 shrink-0" />{ritual.anchor}
            </p>
          )}
          {ritual.intention && (
            <p className="flex items-start gap-2">
              <Target className="w-3.5 h-3.5 mt-0.5 text-amber-300/60 shrink-0" />{ritual.intention}
            </p>
          )}
          {ritual.landing && (
            <p className="flex items-start gap-2">
              <Feather className="w-3.5 h-3.5 mt-0.5 text-amber-300/60 shrink-0" />{ritual.landing}
            </p>
          )}
        </div>

        {ritual.audio_url && (
          <div className="space-y-1.5" data-testid="ritual-audio">
            <span className="flex items-center gap-2 text-xs text-white/55">
              <Headphones className="w-3.5 h-3.5 text-amber-300/70" /> Guided recording
            </span>
            <audio controls src={ritual.audio_url} className="w-full h-9" style={{ colorScheme: 'dark' }}
                   data-testid="ritual-audio-player" />
          </div>
        )}

        {timed && (
          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-white/50">How long did you sit?</span>
              <span className="text-sm text-white">{minutes} of {ritual.duration_minutes} min
                <span className="text-white/40"> · {percent}%</span>
              </span>
            </div>
            <input
              type="range" min="0" max={ritual.duration_minutes} value={minutes}
              onChange={(e) => setMinutes(Number(e.target.value))}
              className="w-full accent-amber-400"
              data-testid="ritual-minutes-slider"
            />
            <p className="text-[11px] text-white/35">
              Showing up for part of it still counts — the mandala fills to the minute.
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => send(timed ? { minutes_done: minutes } : { percent: 100 })}
            disabled={busy}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full
                       bg-amber-400 px-5 py-2.5 text-sm font-semibold text-slate-950
                       hover:bg-amber-300 disabled:opacity-50 transition-colors"
            data-testid="ritual-complete-confirm"
          >
            <Check className="w-4 h-4" /> {timed ? 'Log this' : 'Done'}
          </button>
          {ritual.percent > 0 && (
            <button
              onClick={undo} disabled={busy}
              className="inline-flex items-center justify-center gap-2 rounded-full border
                         border-white/15 px-5 py-2.5 text-sm text-white/70 hover:text-white
                         hover:border-white/30 transition-colors"
              data-testid="ritual-complete-undo"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Undo today
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompleteSheet;
